import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { User, MessageSquare, Calendar, PlusCircle, Mail, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { useTheme } from '@/context/ThemeContext';
import AddNewDialog from '@/components/AddNewDialog';
import QuoteDialog from '@/components/quotes/QuoteDialog';
import { useIsMobile } from '@/hooks/use-mobile';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { Child, getStoredChildren, storeChildren, getStoredQuotes, getAllMemories } from '@/utils/storage';
import { logger } from '@/utils/logger';

const Index = () => {
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  
  const [children, setChildren] = useState<Child[]>([]);
  const [newChild, setNewChild] = useState<Omit<Child, 'id'>>({
    name: '',
    birthdate: '',
    photoUrl: ''
  });
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [todayMemoriesCount, setTodayMemoriesCount] = useState(0);

  useEffect(() => {
    const fetchChildren = async () => {
      const storedChildren = await getStoredChildren();
      setChildren(storedChildren || []);
    };
    
    fetchChildren();

    const fetchQuotes = async () => {
      try {
        const allQuotes = await getStoredQuotes();
        const sorted = [...allQuotes].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        setRecentQuotes(sorted.slice(0, 2));
      } catch (error) {
        logger.error("Error fetching quotes:", error);
        setRecentQuotes([]);
      }
    };

    const fetchTodayMemories = async () => {
      try {
        const allMemories = await getAllMemories();
        const today = new Date();
        const todayMemories = allMemories.filter(memory => {
          const memoryDate = new Date(memory.date);
          return memoryDate.getMonth() === today.getMonth() &&
                 memoryDate.getDate() === today.getDate();
        });
        setTodayMemoriesCount(todayMemories.length);
      } catch (error) {
        logger.error("Error fetching today's memories:", error);
        setTodayMemoriesCount(0);
      }
    };

    fetchQuotes();
    fetchTodayMemories();
  }, []);

  const handleAddChild = async () => {
    if (!newChild.name || !newChild.birthdate) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }
    
    try {
      const newChildWithId = await storeChildren(newChild);
      
      if (newChildWithId) {
        setChildren(prev => [...prev, newChildWithId]);
      }

      setNewChild({ name: '', birthdate: '', photoUrl: '' });
    } catch (error) {
      logger.error("Error adding child:", error);
      toast({
        title: "Error",
        description: "Failed to add child. Please try again.",
        variant: "destructive"
      });
    }
  };
  
  // Get actual children profiles from state
  const childProfiles = children.slice(0, 2);

  return (
    <Layout>
      <div className="pb-16 md:pb-0">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-semibold text-foreground">Dashboard</h1>
          <QuoteDialog>
            <Button className="rounded-full bg-primary text-white hover:bg-primary/90">
              <PlusCircle className="h-4 w-4 mr-1.5" />
              Add Quote
            </Button>
          </QuoteDialog>
        </div>
        
        <section className="mb-10">
          <div className="flex justify-between items-center mb-5">
            <h2 className="text-lg font-medium text-foreground text-left">Recent Quotes</h2>
            <Link to="/quotes" className="text-sm text-primary hover:text-primary/80">
              View All
            </Link>
          </div>
          <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
            {recentQuotes.map((quote) => (
              <Link to={`/quotes?highlight=${quote.id}`} key={quote.id}>
                <Card className="border soft-shadow card-hover">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <MessageSquare className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-foreground mb-2 italic text-left">{quote.content}</p>
                        {quote.context && (
                          <p className="text-sm text-muted-foreground mt-1 text-left">{quote.context}</p>
                        )}
                        <div className="flex items-center mt-2 text-sm text-muted-foreground text-left">
                          <Link to={`/children/${quote.childId}`} className="hover:text-primary">
                            <span className="font-medium">{quote.childName}</span>
                          </Link>
                          <span className="mx-2">•</span>
                          <span>{new Date(quote.date).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
            
            {recentQuotes.length === 0 && (
              <div className="col-span-2 text-center py-6">
                <p className="text-left text-muted-foreground">No quotes yet. Add your first quote!</p>
              </div>
            )}
          </div>
        </section>
        
        <section className="mb-10">
          <div className="flex justify-between items-center mb-5">
            <h2 className="text-lg font-medium text-foreground">Children</h2>
            <Link to="/children" className="text-sm text-primary hover:text-primary/80">
              View All
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {childProfiles.length > 0 ? (
              childProfiles.map((child) => (
                <Link to={`/children/${child.id}`} key={child.id} className="col-span-1 sm:col-span-1 md:col-span-1">
                  <Card className="border soft-shadow card-hover h-full">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-medium text-foreground">{child.name}</h3>
                          <p className="text-sm text-muted-foreground">View profile</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))
            ) : (
              <div className="col-span-full text-center py-6">
                <p className="text-left text-muted-foreground">No children added yet. Add your first child!</p>
              </div>
            )}
            
            <Dialog>
              <DialogTrigger asChild>
                <div className="col-span-1 w-40">
                  <Card className="border soft-shadow card-hover bg-secondary/50 h-full cursor-pointer">
                    <CardContent className={`p-4 ${!isMobile ? 'flex items-center justify-center h-full' : ''}`}>
                      <div className={`flex items-center ${!isMobile ? 'justify-center' : ''} gap-2`}>
                        <div className="h-8 w-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <PlusCircle className="h-4 w-4 text-primary" />
                        </div>
                        <span className="font-medium text-sm text-foreground">Add Child</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Child</DialogTitle>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="name">Name</Label>
                    <Input 
                      id="name" 
                      placeholder="Child's name"
                      value={newChild.name}
                      onChange={(e) => setNewChild({...newChild, name: e.target.value})}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="birthdate">Birthdate</Label>
                    <Input 
                      id="birthdate" 
                      type="date" 
                      value={newChild.birthdate}
                      onChange={(e) => setNewChild({...newChild, birthdate: e.target.value})}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="photo">Photo URL (optional)</Label>
                    <Input 
                      id="photo" 
                      placeholder="https://example.com/photo.jpg"
                      value={newChild.photoUrl}
                      onChange={(e) => setNewChild({...newChild, photoUrl: e.target.value})}
                    />
                  </div>
                  <Button onClick={handleAddChild} className="mt-2">
                    Add Child
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </section>
        
        <section>
          <div className="flex justify-between items-center mb-5">
            <h2 className="text-lg font-medium text-foreground text-left">On This Day</h2>
            <Link to="/on-this-day" className="text-sm text-primary hover:text-primary/80">
              View All
            </Link>
          </div>
          <Link to="/on-this-day">
            <Card className="border soft-shadow card-hover">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Calendar className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex flex-col items-start">
                    <h3 className="font-medium text-foreground mb-2 text-left">Memories from Today</h3>
                    <p className="text-muted-foreground text-sm text-left">
                      {todayMemoriesCount > 0
                        ? `You have ${todayMemoriesCount} ${todayMemoriesCount === 1 ? 'memory' : 'memories'} from this day in previous years`
                        : "Check back to see quotes from this day in previous years"
                      }
                    </p>
                    <Button variant="outline" size="sm" className="mt-4 rounded-full">
                      View Memories
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </section>
      </div>
    </Layout>
  );
};

export default Index;
